import { Tooltip } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import classNames from "classnames";
import Image from "@components/Image";
import type { CharacterAssetRecord } from "@apis/http/api";
import { UIProductSubImages } from "@pages/library/UICardWrap/UIProductSubImages";

interface UICharacterPickCardProps {
  record: CharacterAssetRecord;
  selected: boolean;
  onSelect: (record: CharacterAssetRecord) => void;
}

/**
 * 选择弹窗里的只读角色卡（从 UICharacterCard 抽取视觉，去掉 Edit/Delete/Dropdown）。
 *
 * - 主图 + hover 副图浮层 + 卡片底部 name
 * - BINDING/FAILED 状态徽标，处理中/失败的角色不可选
 * - 选中态用蓝色描边
 */
export const UICharacterPickCard = ({ record, selected, onSelect }: UICharacterPickCardProps) => {
  const bindState = record.bindState;
  const errorMessage = record.platformSummary?.errorMessage;
  const selectable = bindState !== "BINDING" && bindState !== "FAILED";

  return (
    <div className="relative">
      <div
        className={classNames(
          "bg-kds-bg border border-kds-border rounded-[8px] overflow-hidden w-full relative group transition-colors",
          selectable ? "cursor-pointer hover:border-kds-accent" : "cursor-not-allowed opacity-60",
        )}
        onClick={() => {
          if (selectable) onSelect(record);
        }}
      >
        <div className="relative aspect-square w-full z-1">
          <Image className="w-full h-full rounded-none! border-none!" url={record.mainImage?.imageUrl} />
          <UIProductSubImages
            images={(record.otherImages ?? []).map((img) => ({ url: img.imageUrl }))}
            className="hidden group-hover:flex group-hover:inset-x-[4px] group-hover:bottom-[4px]"
          />
        </div>
        <div className="px-[12px] pt-[10px] pb-[12px] text-kds-fg text-[13px] leading-[18px] line-clamp-2">
          {record.name}
        </div>

        {bindState === "BINDING" && (
          <div className="absolute left-[12px] top-[12px] px-[8px] h-[22px] flex items-center rounded-[4px] bg-kds-mask text-white text-[12px]">
            <Trans>Processing</Trans>
          </div>
        )}
        {bindState === "FAILED" && (
          <Tooltip title={errorMessage || t("Binding failed. Please retry.")}>
            <div className="absolute left-[12px] top-[12px] px-[8px] h-[22px] flex items-center rounded-[4px] bg-kds-danger-fg text-white text-[12px]">
              <Trans>Failed</Trans>
            </div>
          </Tooltip>
        )}
      </div>
      <div
        className={classNames(
          "absolute -top-[2px] -right-[2px] -bottom-[2px] -left-[2px] z-10 rounded-[10px] border-2 transition-colors pointer-events-none",
          selected ? "border-kds-accent!" : "border-transparent",
        )}
        aria-hidden={!selected}
        aria-label={t("Selected")}
      />
    </div>
  );
};
